import { CommonModule } from '@angular/common';
import {
	Component,
	computed,
	effect,
	inject,
	model,
	OnInit,
	signal,
} from '@angular/core';
import { EmailTemplateEditorComponent } from '@components/email-template-editor/email-template-editor.component';
import { dispatch, select, Store } from '@ngxs/store';
import { SelectEmailTemplate } from '@state/email-templates/email-templates.actions';
import { DRAWER_DATA } from '@ui/drawer/drawer-data.token';
import { DrawerService } from '@ui/drawer/drawer.service';
import { IconsModule } from '@ui/icons/icons.module';
import { firstValueFrom, lastValueFrom, Observable } from 'rxjs';
import {
	SelectComponent,
	SelectOption,
} from '../../../ui/select/select.component';
import { EmailIntegrationApiService } from '../../api-repos/email-integration/email-integration.api.service';
import { emailTemplatesDrawerViewModel } from './email-templates-drawer.selectors';
import { FormsModule } from '@angular/forms';
import { getEmailBodyFromHtml } from '@core/util/jurisdictions';
import {
	EmailAttachment,
	MatterEmailRequest,
} from '../../api-repos/email-integration/email-integration.models';
import { ICON } from '../../core/util/icon';
import { BusinessFeatureFlagSelectors } from '../../state/business';
import { ToastService } from 'src/app/ui/toast/toast.service';
import { Context } from '@models/matter';
import { environment } from 'src/environments/environment';
import { $intakePageViewModel } from 'src/app/private/sale/parties/client-intake-page/view-models/selectors';
import { TestHookDirective } from '@core/ui';
import { TestHooks } from './email-templates-drawer.hooks';

export type EmailBodyValidatorFn = (convertedEmailBody: string) => boolean;

export interface EmailTemplatesDrawerData {
	templateId?: string;
	templateOptions: SelectOption[];
	to: string[];
	attachments?: EmailAttachment[];
	context?: Context;
	ignoreEmailIntegration?: boolean;
	additionalTags?: Record<string, unknown>;
}

export interface EmailTemplatesDrawerResult {
	sentSuccessfully: boolean;
	error?: string;
	attachments?: EmailAttachment[];
	to?: string[];
	cc?: string[];
	bcc?: string[];
	subject?: string;
	body?: string;
}

@Component({
	selector: 'app-email-templates-drawer',
	templateUrl: './email-templates-drawer.component.html',
	standalone: true,
	imports: [
		CommonModule,
		IconsModule,
		EmailTemplateEditorComponent,
		SelectComponent,
		FormsModule,
		TestHookDirective,
	],
})
export class EmailTemplatesDrawerComponent implements OnInit {
	testHooks = TestHooks;
	private drawerService = inject(DrawerService);
	private emailApiService = inject(EmailIntegrationApiService);
	private toastService = inject(ToastService);
	private clientIntakePage_ = select($intakePageViewModel);
	private drawerData = inject<EmailTemplatesDrawerData>(DRAWER_DATA);

	ICON = ICON;
	ignoreEmailIntegration = this.drawerData.ignoreEmailIntegration ?? false;
	showCc_ = signal(false);
	selectedTemplateOption_ = signal<SelectOption | null>(null);
	templateOptions_ = signal<SelectOption[]>([]);
	content_ = model('');
	subject_ = model('');
	to_ = model<SelectOption[]>([]);
	cc_ = model<SelectOption[]>([]);
	bcc_ = model<SelectOption[]>([]);
	sentSuccessfully_ = model(false);
	errorMessage_ = model<string | null>(null);
	attachments_ = model<EmailAttachment[]>([]);

	viewModel_ = select(emailTemplatesDrawerViewModel);
	setSelectedTemplate = dispatch(SelectEmailTemplate);

	outlookIntegrationEnabled = this.store.selectSnapshot(
		BusinessFeatureFlagSelectors.isOutlookIntegrationEnabled,
	);

	clientIntakeUrl_ = computed(() => {
		return `${environment.clientPortal.baseUrl}/login/invite/${this.clientIntakePage_()?.portalInvite?.id}`;
	});

	constructor(private store: Store) {
		effect(async () => {
			const templateVersion = this.viewModel_().selectedTemplate;
			const selectedTemplateOption = this.templateOptions_().find(
				(t) => t.id === templateVersion?.id,
			);
			this.selectedTemplateOption_.set(selectedTemplateOption);

			if (templateVersion) {
				const convertedContent = await firstValueFrom(
					this.convertHandleBarsContent(templateVersion.htmlMessage),
				);

				this.content_.set(convertedContent);
			}

			if (!templateVersion?.subject) return;

			this.subject_.set(
				await firstValueFrom(
					this.convertHandleBarsContent(templateVersion.subject),
				),
			);
		});
	}

	ngOnInit() {
		const templateId = this.drawerData.templateId;
		const templateOptions = this.drawerData.templateOptions;
		this.templateOptions_.set(templateOptions);
		this.to_.set(
			this.drawerData?.to
				? this.drawerData.to.map((email) => ({ text: email, id: email }))
				: [],
		);
		this.attachments_.set(this.drawerData.attachments || []);
		this.setSelectedTemplate(templateId || templateOptions[0]?.id || null);

		// TODO: Spoke with Farid/Anfernee, this code is not final and will be improved in the future by adding a check to see if the user has connected their Outlook account before using the email integration flow. Intake has borrowed the logic from here and should also be updated when the improvements have been made (search for "Issue #1762797942139")
		if (this.outlookIntegrationEnabled && !this.ignoreEmailIntegration) {
			this.drawerService.setPrimaryAction('Send Email', () =>
				this.onIntegrationSend(),
			);
		} else {
			this.drawerService.setPrimaryAction('Open in Mail', () => this.onSend());
		}
	}

	isValidEmail = (email: string): boolean => {
		const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return emailPattern.test(email);
	};

	onSend() {
		const toEmails = this.to_()
			.map((option) => option.text)
			.join(',');
		const ccEmails = this.cc_()
			.map((option) => option.text)
			.join(',');
		const bccEmails = this.bcc_()
			.map((option) => option.text)
			.join(',');

		const params: string[] = [];
		if (ccEmails) params.push(`cc=${ccEmails}`);
		if (bccEmails) params.push(`bcc=${bccEmails}`);

		const mailtoLink = `mailto:${toEmails}?subject=${encodeURIComponent(this.subject_())}&body=${getEmailBodyFromHtml(this.content_())}&${params.join('&')}`;
		window.location.href = mailtoLink;
		this.toastService.success('Email is being sent...');
		this.sentSuccessfully_.set(true);
		this.close();
	}

	async onTemplateChange(t: SelectOption) {
		this.setSelectedTemplate(t?.id || null);
	}

	toggleCc() {
		this.showCc_.set(!this.showCc_());
	}

	close() {
		this.drawerService.setAfterCloseAction(() => {
			const result: EmailTemplatesDrawerResult = {
				sentSuccessfully: this.sentSuccessfully_(),
				error: this.errorMessage_() || undefined,
				attachments: this.attachments_(),
				to: this.to_().map((option) => option.text),
				cc: this.cc_().map((option) => option.text),
				bcc: this.bcc_().map((option) => option.text),
				subject: this.subject_(),
				body: this.content_(),
			};
			return result;
		});
		this.drawerService.closeDrawer();
	}

	private convertHandleBarsContent(content: string): Observable<string> {
		return this.emailApiService.convertEmailTemplate(
			this.viewModel_().matterId,
			this.viewModel_().businessId,
			content || '',
			this.drawerData.context,
			this.drawerData.additionalTags,
		);
	}

	onContentChange(content: string) {
		this.content_.set(content);
	}

	onIntegrationSend() {
		const emailRequest: MatterEmailRequest = {
			matterId: this.viewModel_().matterId,
			recipients: this.to_().map((option) => ({ email: option.text })),
			cc: this.cc_().map((option) => ({ email: option.text })),
			bcc: this.bcc_().map((option) => ({ email: option.text })),
			subject: this.subject_(),
			htmlMessage: this.content_(),
			attachments: this.attachments_(),
		};
		lastValueFrom(this.emailApiService.sendEmailViaOutlook(emailRequest))
			.then(() => {
				this.toastService.success('Email is being sent...');
				this.sentSuccessfully_.set(true);
				this.close();
			})
			.catch((err) => {
				//Todo: Handle error properly, current endpoint always return 200 even on failure
				this.sentSuccessfully_.set(false);
				this.errorMessage_.set(err.message);
				console.error('Error sending email', err);
				this.toastService.error('Error sending email');
			})
			.finally(() => {
				this.close();
			});
	}
}
